import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { Flame, Loader2, X, Plus } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [kinds, setKinds] = useState([]);
  const [bizName, setBizName] = useState("");
  const [kind, setKind] = useState("");
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");

  useEffect(() => { api.get("/meta/business-kinds").then((r) => setKinds(r.data)).catch(() => {}); }, []);

  const pickKind = (k) => { setKind(k.id); setCats(k.categories); };
  const addCat = () => {
    const c = newCat.trim();
    if (c && !cats.includes(c) && cats.length < 5) setCats([...cats, c]);
    setNewCat("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password, kind && bizName.trim() ? { business_name: bizName.trim(), business_kind: kind, categories: cats } : {});
      navigate("/dashboard/business", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="register-logo">
          <span className="w-9 h-9 rounded-md bg-primary flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></span>
          <span className="font-display text-xl font-bold">DealWisp</span>
        </Link>
        <div className="bg-card border border-white/10 rounded-2xl p-8">
          <h1 className="font-display text-2xl font-bold tracking-tight">Start your free trial</h1>
          <p className="text-sm text-muted-foreground mt-1">14 days free. No card needed.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="overline text-muted-foreground">Your name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="register-name" className="mt-2 bg-background border-white/10" />
            </div>
            <div>
              <Label className="overline text-muted-foreground">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@shop.com" data-testid="register-email" className="mt-2 bg-background border-white/10" />
            </div>
            <div>
              <Label className="overline text-muted-foreground">Password</Label>
              <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="register-password" className="mt-2 bg-background border-white/10" />
            </div>
            <div>
              <Label className="overline text-muted-foreground">Business name</Label>
              <Input value={bizName} onChange={(e) => setBizName(e.target.value)} placeholder="Campus Bites" data-testid="register-bizname" className="mt-2 bg-background border-white/10" />
            </div>
            <div>
              <Label className="overline text-muted-foreground">What do you sell?</Label>
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid="register-kinds">
                {kinds.map((k) => (
                  <button type="button" key={k.id} onClick={() => pickKind(k)} data-testid={`kind-${k.id}`}
                    className={`text-left text-sm rounded-lg border px-3 py-2 transition-colors ${kind === k.id ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}>
                    {k.label}
                    {k.mode === "food" && <span className="block text-[11px] text-primary mt-0.5">Pre-orders + token numbers</span>}
                  </button>
                ))}
              </div>
            </div>
            {kind && (
              <div data-testid="register-categories">
                <Label className="overline text-muted-foreground">Your categories (you can change these later)</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {cats.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 text-xs rounded-full bg-secondary/40 px-3 py-1">
                      {c}<button type="button" aria-label={`remove ${c}`} onClick={() => setCats(cats.filter((x) => x !== c))}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                {cats.length < 5 && (
                  <div className="mt-2 flex gap-2">
                    <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCat(); } }} placeholder="Add a category" className="bg-background border-white/10 h-9" data-testid="register-newcat" />
                    <Button type="button" variant="outline" onClick={addCat} className="h-9 border-white/15"><Plus className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
            )}
            {error && <p className="text-sm text-primary" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-secondary" data-testid="register-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="register-to-login">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
